import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Cloud, ArrowRight, Shield, Database, Network } from "lucide-react";
import PageMeta from "../components/PageMeta";

const PILLARS = [
  {
    icon: Database,
    title: "Unified data layer",
    desc: "One source of truth across Space, Nexus, and Cerebrum. No more siloed exports.",
  },
  {
    icon: Network,
    title: "Edge-aware sync",
    desc: "Built for field operations. Devices sync the moment connectivity returns — no data loss.",
  },
  {
    icon: Shield,
    title: "Sovereign by design",
    desc: "EU and ZA regions on day one. Customer-managed keys for regulated industries.",
  },
];

export default function CloudPage() {
  return (
    <div className="min-h-screen" data-testid="cloud-page">
      <PageMeta
        title="Geminii Cloud"
        description="The unified cloud layer for Geminii Space, Nexus and Cerebrum. Coming 2026."
        path="/cloud"
      />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.06] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-rogue-cyan animate-pulse" />
              <span className="text-[11px] font-mono uppercase tracking-wider text-geminii-muted">
                In development · Early access 2026
              </span>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <Cloud size={36} className="text-rogue-cyan" />
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
                Geminii Cloud
              </h1>
            </div>
            <p className="text-lg md:text-xl text-geminii-muted max-w-2xl leading-relaxed mb-10">
              The connective tissue for every Geminii product. One data layer for Space, Nexus, and Cerebrum — built for operators who need their software to remember everything, everywhere.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="cloud-earlyaccess-cta"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-rogue-cyan text-geminii-deep font-semibold text-sm hover:shadow-[0_0_24px_rgba(31,182,193,0.35)] transition-all duration-250"
              >
                Request early access
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-rogue-cyan/50 hover:text-rogue-cyan transition-all duration-250"
              >
                Back to Geminii
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">
            What it's being built for
          </h2>
          <p className="text-geminii-muted max-w-xl mb-10">
            Geminii Cloud isn't another generic hosting product. It's purpose-built for operations that span the office, the field, and the factory floor.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-geminii-card border border-white/[0.06] rounded-xl p-7"
              >
                <p.icon size={22} className="text-rogue-cyan mb-4" />
                <h3 className="font-display font-bold text-base text-white mb-2">{p.title}</h3>
                <p className="text-sm text-geminii-muted leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-geminii-card border border-rogue-cyan/20 rounded-2xl p-8 md:p-12 flex flex-wrap items-center justify-between gap-6">
            <div className="max-w-xl">
              <h3 className="font-display font-bold text-xl md:text-2xl text-white mb-2">
                Want to shape it?
              </h3>
              <p className="text-sm text-geminii-muted leading-relaxed">
                Geminii is taking on a small group of design partners for the Cloud beta. If your operation needs sovereign data and tight integration across products, get in touch.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-rogue-cyan text-geminii-deep font-semibold text-sm hover:shadow-[0_0_20px_rgba(31,182,193,0.4)] transition-all duration-250"
            >
              Become a design partner
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
